import type { BusinessType } from "../types";
import type { EventStatus } from "../types";

export type EventTutorialFormDefaults = {
  name: string;
  description: string;
  startDate: string;
  endDate: string;
  location: string;
  maxClients: string;
  avgTimePerClient: string;
  status: EventStatus;
  phoneRequired: boolean;
  emailNotifications: boolean;
  whatsappNotifications: boolean;
  pushNotifications: boolean;
  autoSendInvoice: boolean;
  defaultDeliveryMethod: string;
  maxPartySize: string;
};

const toLocalDateTime = (date: Date): string =>
  new Date(date.getTime() - date.getTimezoneOffset() * 60_000)
    .toISOString()
    .slice(0, 16);

const getTomorrowAt = (hours: number, minutes = 0): string => {
  const date = new Date();
  date.setDate(date.getDate() + 1);
  date.setHours(hours, minutes, 0, 0);
  return toLocalDateTime(date);
};

const getTomorrowEndAt = (hours: number, minutes = 0): string => {
  const date = new Date();
  date.setDate(date.getDate() + 1);
  date.setHours(hours, minutes, 0, 0);
  return toLocalDateTime(date);
};

type ExampleConfig = {
  avgTimePerClient: string;
  maxClients: string;
  startHour: number;
  endHour: number;
};

const EXAMPLE_CONFIG: Record<BusinessType, ExampleConfig> = {
  RESTAURANT: { avgTimePerClient: "45", maxClients: "40", startHour: 10, endHour: 14 },
  BEAUTY: { avgTimePerClient: "30", maxClients: "25", startHour: 9, endHour: 18 },
  TATTOO: { avgTimePerClient: "90", maxClients: "20", startHour: 11, endHour: 19 },
  GARAGE: { avgTimePerClient: "60", maxClients: "15", startHour: 8, endHour: 17 },
  OTHER: { avgTimePerClient: "60", maxClients: "30", startHour: 10, endHour: 18 },
};

export const getEventTutorialDefaults = (
  businessType: BusinessType,
  t: (key: string) => string,
  options?: {
    emailNotifications?: boolean;
    whatsappNotifications?: boolean;
  },
): EventTutorialFormDefaults => {
  const type = businessType ?? "OTHER";
  const config = EXAMPLE_CONFIG[type] ?? EXAMPLE_CONFIG.OTHER;
  const prefix = `admin.eventTutorial.examples.${type}`;

  return {
    name: t(`${prefix}.name`),
    description: t(`${prefix}.description`),
    startDate: getTomorrowAt(config.startHour),
    endDate: getTomorrowEndAt(config.endHour),
    location: t(`${prefix}.location`),
    maxClients: config.maxClients,
    avgTimePerClient: config.avgTimePerClient,
    status: "UPCOMING",
    phoneRequired: true,
    emailNotifications: options?.emailNotifications ?? true,
    whatsappNotifications: options?.whatsappNotifications ?? false,
    pushNotifications: true,
    autoSendInvoice: false,
    defaultDeliveryMethod: "NONE",
    maxPartySize: type === "RESTAURANT" ? "8" : "1",
  };
};

export const TUTORIAL_STEP_TARGETS = [
  "intro",
  "basics",
  "schedule",
  "details",
  "notifications",
  "submit",
] as const;

export type TutorialStepTarget = (typeof TUTORIAL_STEP_TARGETS)[number];

export const TOTAL_TUTORIAL_STEPS = TUTORIAL_STEP_TARGETS.length;
